import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Edit, 
  Download,
  Camera,
  CreditCard,
  Plane,
  Home,
  Flag,
  Image as ImageIcon,
  X,
  Lock,
  Send,
  XCircle,
  Upload,
  Trash2
} from 'lucide-react';
import { guestService } from '../services/guestService';
import { Guest, GuestAnomaly, GuestHistoryEntry, CheckOutData } from '../types/guest';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import CheckoutModal from '../components/guest/CheckoutModal';
import IdentityPhotoViewer from '../components/guest/IdentityPhotoViewer';
import AnomaliesTab from '../components/guest/AnomaliesTab';
import HistoryTab from '../components/guest/HistoryTab';
import { useIsMounted } from '../hooks/useIsMounted';

const GuestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guest, setGuest] = useState<Guest | null>(null);
  const [anomalies, setAnomalies] = useState<GuestAnomaly[]>([]);
  const [history, setHistory] = useState<GuestHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'anomalies' | 'history' | 'documents'>('details');
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const isMounted = useIsMounted();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Identifiant de l\'invité manquant');
      return;
    }

    let cancelled = false;

    const loadGuestData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load guest data first (required), then load secondary data independently
        const guestData = await guestService.getGuest(id);
        if (cancelled) return;
        setGuest(guestData);

        // Load anomalies and history independently - don't block on failure
        const [anomaliesData, historyData] = await Promise.allSettled([
          guestService.getGuestAnomalies(id),
          guestService.getGuestHistory(id)
        ]);

        if (cancelled) return;
        if (anomaliesData.status === 'fulfilled') {
          setAnomalies(anomaliesData.value);
        }
        if (historyData.status === 'fulfilled') {
          setHistory(historyData.value);
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement des données:', err);
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 404) {
            setError('Invité non trouvé (ID: ' + id + ')');
          } else if (status === 401 || status === 403) {
            setError('Accès non autorisé. Veuillez vous reconnecter.');
          } else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error')) {
            setError('Impossible de contacter le serveur. Vérifiez que les services backend sont démarrés sur le port 8080.');
          } else {
            setError('Erreur lors du chargement des données de l\'invité: ' + (err?.message || 'erreur inconnue'));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGuestData();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleCheckOut = async (checkOutData: CheckOutData) => {
    if (!guest) return;

    try {
      setCheckOutLoading(true);

      // Convert invoice file to base64 if present
      let invoiceBase64 = '';
      let invoiceFilename = '';
      if (checkOutData.invoiceFile) {
        invoiceFilename = checkOutData.invoiceFile.name;
        invoiceBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data URL prefix (e.g., "data:application/pdf;base64,")
            const base64 = result.split(',')[1] || result;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(checkOutData.invoiceFile!);
        });
      }

      await guestService.checkOut({
        guestId: guest.id,
        checkOutDate: checkOutData.checkOutDate,
        finalRoomNumber: checkOutData.finalRoomNumber,
        notes: checkOutData.notes,
        accommodationAmount: checkOutData.invoiceAmount,
        amountType: checkOutData.amountType || 'TTC',
        departureMode: checkOutData.departureMode,
        invoice: invoiceBase64,
        invoiceFilename,
        additionalCharges: checkOutData.additionalCharges,
        damageReport: checkOutData.damageReport,
        satisfactionRating: checkOutData.satisfactionRating,
        feedback: checkOutData.feedback,
      });
      
      if (!isMounted()) return;
      
      // Recharger les données
      const updatedGuest = await guestService.getGuest(guest.id);
      if (isMounted()) {
        setGuest(updatedGuest);
        setShowCheckOutModal(false);
      }
      
      // Recharger l'historique
      const updatedHistory = await guestService.getGuestHistory(guest.id);
      if (isMounted()) {
        setHistory(updatedHistory);
      }
    } catch (err) {
      console.error('Erreur lors du check-out:', err);
      if (isMounted()) {
        alert('Erreur lors du check-out');
      }
    } finally {
      if (isMounted()) setCheckOutLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!guest || !deleteReason.trim()) return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const resp = await fetch('/api/v1/admin/deletion-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          checkin_id: guest.id,
          police_file_number: guest.policeFileNumber || '',
          guest_name: guest.firstName + ' ' + guest.lastName,
          reason: deleteReason.trim(),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.error || 'Erreur lors de la demande');
      }
      setShowDeleteModal(false);
      setDeleteReason('');
      setDeleteSuccess('Demande de suppression envoyée. En attente de validation par l\'administrateur.');
      setTimeout(() => setDeleteSuccess(''), 8000);
      // Refresh guest data
      const updated = await guestService.getGuest(guest.id);
      if (isMounted()) setGuest(updated);
    } catch (err: any) {
      alert(err.message || 'Erreur lors de la demande de suppression');
    } finally {
      if (isMounted()) setDeleteLoading(false);
    }
  };

  const handleResolveAnomaly = async (anomalyId: string, resolution: string): Promise<void> => {
    if (!guest) return;

    try {
      await guestService.resolveAnomaly(guest.id, anomalyId, resolution);
      
      if (!isMounted()) return;
      
      const updatedAnomalies = await guestService.getGuestAnomalies(guest.id);
      if (isMounted()) {
        setAnomalies(updatedAnomalies);
      }
      
      // Mettre à jour le guest
      const updatedGuest = await guestService.getGuest(guest.id);
      if (isMounted()) {
        setGuest(updatedGuest);
      }
    } catch (err) {
      console.error('Erreur lors de la résolution de l\'anomalie:', err);
      if (isMounted()) {
        alert('Erreur lors de la résolution de l\'anomalie');
      }
      throw err;
    }
  };

  // Handler for ignoring an anomaly (Requirement 2.5)
  const handleIgnoreAnomaly = async (anomalyId: string, reason: string): Promise<void> => {
    if (!guest) return;

    try {
      // Use the same API but with 'ignored' status
      await guestService.resolveAnomaly(guest.id, anomalyId, `[IGNORÉE] ${reason}`);
      
      if (!isMounted()) return;
      
      const updatedAnomalies = await guestService.getGuestAnomalies(guest.id);
      if (isMounted()) {
        setAnomalies(updatedAnomalies);
      }
      
      // Mettre à jour le guest
      const updatedGuest = await guestService.getGuest(guest.id);
      if (isMounted()) {
        setGuest(updatedGuest);
      }
    } catch (err) {
      console.error('Erreur lors de l\'ignorance de l\'anomalie:', err);
      if (isMounted()) {
        alert('Erreur lors de l\'ignorance de l\'anomalie');
      }
      throw err;
    }
  };

  // Handler for re-uploading identity photo (Requirement 2.4)
  const handleIdentityPhotoReupload = async (file: File): Promise<void> => {
    if (!guest) return;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result as string;
          const updatedGuest = await guestService.updateGuest(guest.id, { documentPhotoUrl: base64 });
          
          if (isMounted()) {
            setGuest(updatedGuest);
          }
          resolve();
        } catch (err) {
          console.error('Erreur lors de la mise à jour de la photo:', err);
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Erreur de lecture du fichier'));
      reader.readAsDataURL(file);
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CHECKED_IN':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'CHECKED_OUT':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'NO_SHOW':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'CHECKED_IN':
        return 'Enregistré';
      case 'CHECKED_OUT':
        return 'Parti';
      case 'NO_SHOW':
        return 'Absent';
      case 'RESERVED':
        return 'Réservé';
      default:
        return 'En attente';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'CHECKED_IN':
        return 'text-green-700 bg-green-100';
      case 'CHECKED_OUT':
        return 'text-gray-700 bg-gray-100';
      case 'NO_SHOW':
        return 'text-red-700 bg-red-100';
      case 'RESERVED':
        return 'text-blue-700 bg-blue-100';
      default:
        return 'text-yellow-700 bg-yellow-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails de l'invité...</p>
          <p className="text-gray-400 text-sm mt-2">Si le chargement persiste, vérifiez la connexion au serveur.</p>
        </div>
      </div>
    );
  }

  if (error || !guest) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error || 'Invité non trouvé'}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/guests')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour à la liste
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/guests')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à la liste
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {guest.firstName} {guest.lastName}
                </h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {/* Police file status */}
                  {guest.policeFileStatus && (
                    <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                      guest.policeFileStatus === 'Ouverte' ? 'bg-yellow-100 text-yellow-700' :
                      guest.policeFileStatus === 'Validée' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {guest.policeFileStatus === 'Clôturée' && <Lock className="w-3 h-3" />}
                      Fiche: {guest.policeFileStatus}
                    </span>
                  )}
                  
                  {/* Guest status */}
                  {getStatusIcon(guest.status)}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(guest.status)}`}>
                    {getStatusLabel(guest.status)}
                  </span>
                  
                  {guest.hasAlerts && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      {guest.alertCount} anomalie{guest.alertCount! > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {guest.status === 'CHECKED_IN' && guest.policeFileStatus !== 'Clôturée' && (
                <button
                  onClick={() => setShowCheckOutModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Check-out
                </button>
              )}
              <button
                onClick={() => navigate(`/guests/${guest.id}/edit`)}
                disabled={guest.policeFileStatus === 'Clôturée'}
                className={`p-2 transition-colors ${
                  guest.policeFileStatus === 'Clôturée'
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={guest.policeFileStatus === 'Clôturée' ? 'Impossible de modifier une fiche clôturée' : 'Modifier'}
              >
                {guest.policeFileStatus === 'Clôturée' ? <Lock className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
              </button>
              {(guest.status as string) !== 'SUPPRIMEE' && (guest.status as string) !== 'DELETION_PENDING' && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm"
                  title="Demander la suppression de cette fiche"
                >
                  <Trash2 className="w-4 h-4" /> Demander la suppression
                </button>
              )}
              {(guest.status as string) === 'DELETION_PENDING' && (
                <span className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm">
                  Suppression en attente
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                Détails
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'documents'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('anomalies')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'anomalies'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                Anomalies ({anomalies.length})
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                Historique
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Informations personnelles */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations personnelles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Nom complet</p>
                        <p className="font-medium text-gray-900">{guest.firstName} {guest.lastName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Date de naissance</p>
                        <p className="font-medium text-gray-900">
                          {guest.dateOfBirth ? format(new Date(guest.dateOfBirth), 'dd/MM/yyyy', { locale: fr }) : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Flag className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Nationalité</p>
                        <p className="font-medium text-gray-900">{guest.nationality || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900">{guest.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Téléphone</p>
                        <p className="font-medium text-gray-900">{guest.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document d'identité */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Document d'identité</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Numéro de passeport</p>
                        <p className="font-medium text-gray-900">{guest.passportNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Date d'expiration</p>
                        <p className="font-medium text-gray-900">
                          {guest.passportExpiry ? format(new Date(guest.passportExpiry), 'dd/MM/yyyy', { locale: fr }) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {guest.documentPhotoUrl && (
                    <div className="mt-4">
                      <IdentityPhotoViewer
                        photoUrl={guest.documentPhotoUrl}
                        canEdit={guest.policeFileStatus !== 'Clôturée'}
                        onReupload={handleIdentityPhotoReupload}
                        title="Scan du document d'identité"
                      />
                    </div>
                  )}
                </div>

                {/* Séjour */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Séjour</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Chambre</p>
                        <p className="font-medium text-gray-900">{guest.roomNumber || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Arrivée</p>
                        <p className="font-medium text-gray-900">
                          {guest.checkInDate ? format(new Date(guest.checkInDate), 'dd/MM/yyyy', { locale: fr }) : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Départ</p>
                        <p className="font-medium text-gray-900">
                          {guest.checkOutDate ? format(new Date(guest.checkOutDate), 'dd/MM/yyyy', { locale: fr }) : '-'}
                        </p>
                      </div>
                    </div>
                    {guest.policeFileNumber && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Fiche police</p>
                          <p className="font-medium text-gray-900">{guest.policeFileNumber}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statut de synchronisation */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Statut de synchronisation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">CNS</span>
                        {guest.cnsTransmittedAt ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      {guest.cnsTransmittedAt ? (
                        <p className="text-xs text-gray-600">
                          Transmis le {format(new Date(guest.cnsTransmittedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">Non transmis</p>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">ONT</span>
                        {guest.ontTransmittedAt ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      {guest.ontTransmittedAt ? (
                        <p className="text-xs text-gray-600">
                          Transmis le {format(new Date(guest.ontTransmittedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">Non transmis</p>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">DGI</span>
                        {guest.dgiTransmittedAt ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      {guest.dgiTransmittedAt ? (
                        <p className="text-xs text-gray-600">
                          Transmis le {format(new Date(guest.dgiTransmittedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">Non transmis</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Indicateur de fichier clôturé */}
                {guest.policeFileStatus === 'Clôturée' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-gray-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">Fiche clôturée</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Cette fiche police est clôturée. Les modifications ne sont plus autorisées.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Documents et photos</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Identity document photo - Using IdentityPhotoViewer component (Requirement 2.4) */}
                  <IdentityPhotoViewer
                    photoUrl={guest.documentPhotoUrl}
                    canEdit={guest.policeFileStatus !== 'Clôturée'}
                    onReupload={handleIdentityPhotoReupload}
                    title="Photo de la pièce d'identité"
                  />

                  {/* Face photo */}
                  {guest.facePhotoBase64 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Photo du visage</h4>
                      <div 
                        className="relative border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                        onClick={() => setEnlargedImage(guest.facePhotoBase64!)}
                      >
                        <img 
                          src={guest.facePhotoBase64} 
                          alt="Photo du visage" 
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Signature */}
                  {guest.signatureBase64 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Signature</h4>
                      <div 
                        className="relative border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors bg-white"
                        onClick={() => setEnlargedImage(guest.signatureBase64!)}
                      >
                        <img 
                          src={guest.signatureBase64} 
                          alt="Signature" 
                          className="w-full h-48 object-contain p-4"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Invoice */}
                  {guest.invoiceUrl && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Facture</h4>
                      <div 
                        className="relative border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                        onClick={() => setEnlargedImage(guest.invoiceUrl!)}
                      >
                        <img 
                          src={guest.invoiceUrl} 
                          alt="Facture" 
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Empty state - only show if no documents at all */}
                {!guest.documentPhotoUrl && !guest.facePhotoBase64 && !guest.signatureBase64 && !guest.invoiceUrl && (
                  <div className="text-center py-12">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun document</h3>
                    <p className="text-gray-600">Aucun document n'a été téléchargé pour cet invité.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'anomalies' && (
              <AnomaliesTab
                anomalies={anomalies}
                onResolve={handleResolveAnomaly}
                onIgnore={handleIgnoreAnomaly}
                loading={loading}
              />
            )}

            {activeTab === 'history' && (
              <HistoryTab
                history={history}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal d'agrandissement d'image */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <button
            onClick={() => setEnlargedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-900" />
          </button>
          <img 
            src={enlargedImage} 
            alt="Agrandissement" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Success banner */}
      {deleteSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 text-sm">
          <CheckCircle className="w-4 h-4" /> {deleteSuccess}
        </div>
      )}

      {/* Modal de check-out */}
      {showCheckOutModal && (
        <CheckoutModal
          isOpen={showCheckOutModal}
          onClose={() => setShowCheckOutModal(false)}
          onConfirm={handleCheckOut}
          guestName={`${guest.firstName} ${guest.lastName}`}
          roomNumber={guest.roomNumber}
          currency={guest.currency}
          loading={checkOutLoading}
          isTaxExempt={(guest as any).isTaxExempt || false}
        />
      )}

      {/* Modal de demande de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Demander la suppression</h3>
                <p className="text-sm text-gray-500">Fiche {guest.policeFileNumber}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
              <strong>Important :</strong> Cette action ne supprime pas immediatement la fiche.
              La demande sera soumise au Super Administrateur pour approbation.
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif de la demande <span className="text-red-500">*</span>
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Indiquez la raison de la suppression..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteReason(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleRequestDeletion}
                disabled={deleteLoading || !deleteReason.trim()}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteLoading ? 'Envoi...' : <><Trash2 className="w-4 h-4" /> Envoyer la demande</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestDetailPage;
